import {
  AudioType,
  DesignChannelType,
  DesignLanguage,
  DesignModuleNode,
  Public,
  TextToSpeechVoice,
  Voice
} from '../types';
import {
  filterNull,
  getCyrb53,
  getInputsFromText,
  getPromptText,
  getVariablesFromText,
  VariableParsing
} from '../utilities';
import { ErrorMessageService } from './error-message-service';
import { IHttpService } from './http-service';
import { HttpMethod, TextToPromptGetAudioProps } from './types';
import { IWorkflowService } from './workflow-service';

const audioHashPrefix = 'promptAudio-';

/**
 * Some legacy voices were known by different codes on Designs in D2 and in TTP.
 * This mapping goes from Design codes (keys) to TTP codes (values) and should not have new values added to it.
 */
const ttpVoiceMap = new Map<TextToSpeechVoice, string>([
  [TextToSpeechVoice.ashleyEnglishUs, 'Ashley'],
  [TextToSpeechVoice.bridgetEnglishUk, 'Bridget'],
  [TextToSpeechVoice.franciscoSpanishUs, 'Francisco'],
  [TextToSpeechVoice.jamesEnglishUs, 'James'],
  [TextToSpeechVoice.kateEnglishUs, 'Kate'],
  [TextToSpeechVoice.leoFrenchCa, 'Leo'],
  [TextToSpeechVoice.paulEnglishUs, 'Paul'],
  [TextToSpeechVoice.philEnglishUs, 'Phil'],
  [TextToSpeechVoice.violetaSpanishUs, 'Violeta']
]);

type PrivateGetTtpAudioProps = (
  Pick<TextToPromptGetAudioProps, 'language' | 'node' | 'nodeModule' | 'serialized'>
  & {
    ttsOnly: boolean;
    ttsText: string;
  }
);

/** Public interface of @see TextToPromptService. */
export type ITextToPromptService = Public<TextToPromptService>;

/** Service used to connect to all AVS APIs. */
export class TextToPromptService {
  /** Base URL to add to all HTTP requests. */
  static readonly baseUrl = '/ttp/ttp-connector-webapp';

  private _httpService: IHttpService;
  private _errorMessageService: ErrorMessageService;
  private _workflowService: IWorkflowService;

  constructor(
    errorMessageService: ErrorMessageService,
    httpService: IHttpService,
    workflowService: IWorkflowService
  ) {
    this._errorMessageService = errorMessageService;
    this._httpService = httpService;
    this._workflowService = workflowService;
  }

  convertTextToSpeechVoice(voice: TextToSpeechVoice | undefined): string | null {
    if (!voice) {
      return null;
    } else if (ttpVoiceMap.has(voice)) {
      return ttpVoiceMap.get(voice)!;
    }
    return voice;
  }

  async getAudioAndType({
    doFetch = true,
    language,
    node,
    nodeModule,
    serialized
  }: TextToPromptGetAudioProps): Promise<[string | null, AudioType | null]> {
    let {
      ttsText,
      ttsHash,
      ttsVoice,
      vtHash,
      vtText,
      vtVoice
    } = this.getPromptTextAndHash(language, node);

    // If there is text & voice, then there will be a hash.
    if (!(ttsHash ?? vtHash)) {
      if (doFetch) {
        const error = new Error('No text to generate audio with.');
        this._errorMessageService.addError(error);
      }
      return [null, null];
    }

    let audioType: AudioType | undefined;

    // Check for cached audio.
    let storedAudio;
    if (vtHash) {
      audioType = AudioType.ttp;
      storedAudio = localStorage.getItem(vtHash);
    }
    if (!storedAudio && ttsHash) {
      audioType = AudioType.tts;
      storedAudio = localStorage.getItem(ttsHash);
    }

    let ttsOnly = true;
    if (!storedAudio) {
      audioType = undefined;
    } else {
      try {
        const storedAudioObject = JSON.parse(storedAudio);
        if (storedAudioObject.url) {
          return [storedAudioObject.url, audioType!];
        }
        if ('ttsOnly' in storedAudioObject) {
          ttsOnly = storedAudioObject.ttsOnly;
        }
      } catch (e) {
        console.error(`Failed to parse stored audio value.${doFetch && ' Now fetching from the server...'}`, e);
      }
    }

    // Look for WFS audio & TTP recordings before generating TTS or dismissing it.

    // If the text changes, the component will call this function with !doFetch.
    // If doFetch is true, it means the user clicked the button to generate audio afterwards.
    if (!doFetch) {
      let promptStatuses = await this._workflowService.getPromptStatus(node.name ?? '', serialized);
      let promptStatus = promptStatuses?.voices?.find(({
        text,
        voice
      }) => {
        if (text === vtText && voice === vtVoice) {
          audioType = AudioType.ttp;
          return true;
        }
        if (text === ttsText && voice === ttsVoice) {
          audioType = AudioType.tts;
          return true;
        }
      }) ?? null;

      const hash = (audioType === AudioType.ttp) ? vtHash! : ttsHash!;
      if (audioType === AudioType.ttp) {
        ttsOnly = false;
      }
      if (promptStatus?.audioUrl) {
        // Handle static prompts.
        const url = promptStatus.audioUrl.replace('{{LINKSTORE_PARAM}}', '/audio/linkstore/');
        localStorage.setItem(hash, JSON.stringify({ url, ttsOnly }));
        return [url, audioType!];
      }
      localStorage.setItem(hash, JSON.stringify({ ttsOnly }));
      return [null, null];
    }

    // No TTS voice set & voice talent has not recorded yet.
    if (ttsOnly && !ttsHash) {
      const error = new Error('No matching recorded content or TTS voice.');
      this._errorMessageService.addError(error);
      return [null, null];
    }

    const url = await this._getTtpAudio({
      language,
      node,
      nodeModule,
      serialized,
      ttsOnly,
      ttsText
    });

    localStorage.setItem((ttsOnly) ? ttsHash! : vtHash!, JSON.stringify({ url, ttsOnly }));

    return [url, ttsOnly ? AudioType.tts : AudioType.ttp];
  }

  getPromptTextAndHash(language: DesignLanguage, node: DesignModuleNode) {
    const {
      language: languageCode,
      ttsFallBackVoice: ttsVoice,
      voice: vtVoice
    } = language;
    let getTextConfig = {
      channel: DesignChannelType.voice,
      languageCode,
      node
    };
    let vtText = getPromptText({ ...getTextConfig, variableParsing: VariableParsing.values });
    let ttsText = getPromptText({ ...getTextConfig, variableParsing: VariableParsing.speakAs });
    // We want to make sure not only the text and variable values (or default values) are the same, but also the voice.
    const ttsHash = (!ttsText || !ttsVoice) ? null : (
      audioHashPrefix + getCyrb53(ttsText + ttsVoice).toString()
    );
    const vtHash = (!vtText || !vtVoice) ? null : (
      audioHashPrefix + getCyrb53(vtText + vtVoice).toString()
    );

    return {
      ttsHash,
      ttsText,
      ttsVoice,
      vtHash,
      vtText,
      vtVoice
    };
  }

  removeAudioFromLocalStorage(language: DesignLanguage, node: DesignModuleNode) {
    const { ttsHash, vtHash } = this.getPromptTextAndHash(language, node);
    if (ttsHash) {
      localStorage.removeItem(ttsHash);
    }
    if (vtHash) {
      localStorage.removeItem(vtHash);
    }
  }

  private async _getTtpAudio({
    language,
    node,
    nodeModule,
    serialized,
    ttsOnly,
    ttsText
  }: PrivateGetTtpAudioProps): Promise<string | null> {
    const { language: languageCode, ttsFallBackVoice, voice } = language;
    const ttsVoice = this.convertTextToSpeechVoice(ttsFallBackVoice);
    const {
      applicationVersion,
      clientName,
      designName
    } = serialized;

    // Prompt units has to use variables because it keeps duplicates.
    const languageInfo = {
      channel: DesignChannelType.voice,
      languageCode,
      node,
      nodeModule,
      serialized
    };
    const inputs = getInputsFromText(languageInfo);
    const variables = getVariablesFromText(languageInfo);
    const promptUnits = variables.map((variable) => {
      const input = inputs.find((input) => {
        return input.varName === variable.fullName;
      });
      return input?.defaultValue ?? '<unkown>';
    }).join('|');

    const response = await this._httpService.fetch({
      query: filterNull({
        branch: applicationVersion,
        client: clientName,
        language: languageCode,
        module: 'promptdefs',
        project: `${clientName}${designName}`,
        promptName: node.name,
        promptUnits,
        ttsFallbackText: ttsText,
        ttsFallbackVoice: ttsVoice,
        ttsOnly: `${ttsOnly}`,
        useTtsFallback: (!!ttsVoice).toString(),
        versionName: `${voice}_${languageCode}`.toUpperCase(),
        voice: voice ?? Voice.phil
      }),
      method: HttpMethod.post,
      url: 'tts'
    });

    if (!response.ok) {
      const error = new Error('Something went wrong trying to generate the audio. Please try again later.');
      this._errorMessageService.addError(error);
      return null;
    }

    const url = (await response.text()).replace(
      /.*\/ttp-connector-webapp/,
      TextToPromptService.baseUrl
    );
    return url;
  }
}
